import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { GEREKCE_AGIR_EN_AZ } from "@koclukpanel/contracts";
import { Alan, BosDurum, Buton, Iskelet, Kart, MetinAlani, Secim } from "@koclukpanel/ui";
import {
  ApiHatasi,
  kagitOnayHedefi,
  kagitOnayKaydet,
  type KagitOnayFormu,
  type KagitRizaTuru,
} from "./api";
import { OgrenciSecici } from "./OgrenciSecici";
import { useSudoKapisi } from "./SudoKapisi";
import { kagitOnayHazirMi } from "./kagit-onay-formu";

const ONAY_ADI: Record<KagitRizaTuru, string> = {
  PRIVACY_NOTICE: "Aydınlatma metni",
  DATA_TRANSFER_ABROAD: "Yurt dışına aktarım",
  PARENT_GUARANTEE: "Veli muvafakati",
};

/** F-LGL-03c — seçim, imzalı belge ve belge üzerindeki sürüm mevcut kâğıt onayı ucuna bağlanır. */
export function KagitVeliOnayi() {
  const [ogrenci, setOgrenci] = useState<{ id: string; ad: string } | null>(null);
  if (!ogrenci)
    return (
      <OgrenciSecici
        baslik="Kâğıt veli onayı"
        aciklama="Islak imzalı onayın ilgili olduğu öğrenciyi seçin."
        onSec={(id, ad) => setOgrenci({ id, ad })}
      />
    );
  return (
    <div className="flex flex-col gap-3">
      <Buton gorunum="sessiz" onClick={() => setOgrenci(null)}>
        Öğrenci değiştir
      </Buton>
      <KagitOnayFormuEkrani key={ogrenci.id} ogrenciId={ogrenci.id} ogrenciAdi={ogrenci.ad} />
    </div>
  );
}

function KagitOnayFormuEkrani({
  ogrenciId,
  ogrenciAdi,
}: {
  ogrenciId: string;
  ogrenciAdi: string;
}) {
  const qc = useQueryClient();
  const { hataYakala, sorguKapisi, kapi } = useSudoKapisi();
  const [veliUserId, setVeliUserId] = useState("");
  const [surumler, setSurumler] = useState<Partial<Record<KagitRizaTuru, string>>>({});
  const [belge, setBelge] = useState<File | null>(null);
  const [gerekce, setGerekce] = useState("");
  const hedef = useQuery({
    queryKey: ["kagit-onay-hedefi", ogrenciId],
    queryFn: () => kagitOnayHedefi(ogrenciId),
    retry: false,
  });
  const kaydet = useMutation<{ denetimYazildi: boolean }, unknown, KagitOnayFormu>({
    mutationFn: kagitOnayKaydet,
    onError: (hata, veri) => hataYakala(() => kaydet.mutate(veri))(hata),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["ogrenciler"] });
      await qc.invalidateQueries({ queryKey: ["onboarding", ogrenciId] });
    },
  });
  if (hedef.isPending) return <Iskelet satir={4} />;
  if (hedef.isError) {
    const sudo = sorguKapisi(hedef.error, () => void hedef.refetch());
    if (sudo) return sudo;
    return (
      <BosDurum
        ton="hata"
        baslik="Onay bilgileri alınamadı"
        aciklama={hedef.error instanceof Error ? hedef.error.message : undefined}
        eylem={<Buton onClick={() => void hedef.refetch()}>Tekrar dene</Buton>}
      />
    );
  }
  if (kaydet.isSuccess)
    return (
      <Kart baslik="Kâğıt veli onayı kaydedildi">
        <p>Belge ve seçilen onaylar {ogrenciAdi} için kaydedildi.</p>
        {!kaydet.data.denetimYazildi && (
          <p role="status">İşlem kaydedildi. Denetim kaydı sunucuda tamamlanmayı bekliyor.</p>
        )}
      </Kart>
    );
  const kayitlar = hedef.data.metinler
    .filter((m) => surumler[m.tur] !== undefined)
    .map((m) => ({ tur: m.tur, surum: surumler[m.tur]!.trim() }));
  const hazir = kagitOnayHazirMi(belge !== null, gerekce, kayitlar, hedef.data.metinler);
  return (
    <Kart baslik={`Kâğıt veli onayı · ${ogrenciAdi}`}>
      {kapi}
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!hazir || !belge || kaydet.isPending) return;
          const veri: KagitOnayFormu = {
            ogrenciUserId: hedef.data.ogrenciUserId,
            veliUserId: veliUserId || null,
            kayitlar,
            gerekce: gerekce.trim(),
            belge,
          };
          kaydet.mutate(veri);
        }}
      >
        <p>
          Yalnızca imzalı belgede bulunan onayları seçin ve belgenin üzerindeki sürümü yazın.
          Yayındaki sürümle farklıysa güncel metnin yeniden imzalanması gerekir.
        </p>
        <Secim
          etiket="İmzalayan kayıtlı veli (isteğe bağlı)"
          value={veliUserId}
          onChange={(e) => setVeliUserId(e.target.value)}
        >
          <option value="">Kayıtlı veli seçmeden devam et</option>
          {hedef.data.veliler.map((v) => (
            <option key={v.userId} value={v.userId}>
              {v.ad}
            </option>
          ))}
        </Secim>
        {hedef.data.metinler.map((m) => (
          <div key={m.tur} className="flex flex-col gap-2">
            <label>
              <input
                type="checkbox"
                checked={surumler[m.tur] !== undefined}
                onChange={(e) =>
                  setSurumler((s) => ({ ...s, [m.tur]: e.target.checked ? "" : undefined }))
                }
              />{" "}
              {ONAY_ADI[m.tur]} · Yayındaki sürüm: {m.surum}
            </label>
            {surumler[m.tur] !== undefined && (
              <Alan
                etiket={`${ONAY_ADI[m.tur]} — belgedeki sürüm`}
                value={surumler[m.tur] ?? ""}
                maxLength={40}
                onChange={(e) => setSurumler((s) => ({ ...s, [m.tur]: e.target.value }))}
              />
            )}
          </div>
        ))}
        {!hedef.data.metinler.length && (
          <p role="alert">Yayında onay metni bulunmuyor. Metinler yayınlanmadan kayıt açılamaz.</p>
        )}
        <Alan
          etiket="İmzalı belgenin taraması"
          type="file"
          accept="application/pdf,image/jpeg,image/png"
          onChange={(e) => setBelge(e.target.files?.[0] ?? null)}
        />
        <MetinAlani
          etiket="Belgenin nasıl alındığına dair gerekçe"
          value={gerekce}
          onChange={(e) => setGerekce(e.target.value)}
          minLength={GEREKCE_AGIR_EN_AZ}
          required
        />
        {kaydet.isError && !(kaydet.error instanceof ApiHatasi && kaydet.error.sudoGerekliMi) && (
          <p role="alert">
            {kaydet.error instanceof Error ? kaydet.error.message : "Onay kaydedilemedi."}
          </p>
        )}
        <Buton type="submit" disabled={!hazir} yukleniyor={kaydet.isPending}>
          Kâğıt onayı kaydet
        </Buton>
      </form>
    </Kart>
  );
}
